import CheckIcon from '@mui/icons-material/Check'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'

import { COMMON, STAGE_EDITOR } from '../labels'
import { api, ApiError } from '../lib/api'
import type { StagePlan } from '../lib/types'
import { FONT_MONO, INK_FAINT, INK_SOFT, LINE, monoLabel } from '../theme'

interface Props {
  cycleId: string
  period: string
  today: string
  stage: StagePlan
  onSaved: () => void
}

/**
 * Captura de lo que REALMENTE pasó en una etapa.
 *
 * Hasta ahora esto sólo existía por consola, así que el plan y la realidad se
 * separaban en cuanto nadie corría el comando. El caso normal —enterarse de que
 * una etapa cerró— se resuelve con un clic ("Terminada hoy"); las fechas finas
 * quedan abajo para cuando se captura en retrospectiva, que es lo habitual.
 */
export function StageEditor({ cycleId, period, today, stage, onSaved }: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [started, setStarted] = useState('')
  const [finished, setFinished] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function open(e: React.MouseEvent<HTMLElement>) {
    // Se precargan las fechas ya capturadas, no las proyectadas: mostrar una
    // proyección en un campo editable invita a guardarla como si fuera un hecho.
    setStarted(stage.actual_start ?? '')
    setFinished(stage.actual_finish ?? '')
    setNote('')
    setError(null)
    setAnchor(e.currentTarget)
  }

  function close() {
    setAnchor(null)
  }

  async function save(body: { started_on?: string; finished_on?: string; note?: string }) {
    setBusy(true)
    setError(null)
    try {
      await api.setStage(cycleId, period, stage.id, body)
      close()
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : COMMON.error)
    } finally {
      setBusy(false)
    }
  }

  async function saveForm() {
    if (!started && !finished) {
      setError(STAGE_EDITOR.nothingToSave)
      return
    }
    if (started && finished && finished < started) {
      setError(STAGE_EDITOR.finishBeforeStart)
      return
    }
    await save({
      ...(started ? { started_on: started } : {}),
      ...(finished ? { finished_on: finished } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    })
  }

  async function clear() {
    setBusy(true)
    setError(null)
    try {
      await api.clearStage(cycleId, period, stage.id)
      close()
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : COMMON.error)
    } finally {
      setBusy(false)
    }
  }

  const captured = Boolean(stage.actual_start || stage.actual_finish)

  return (
    <>
      <Tooltip title={STAGE_EDITOR.open}>
        <IconButton
          size="small"
          onClick={open}
          aria-label={`${STAGE_EDITOR.open}: ${stage.name}`}
          sx={{ color: captured ? INK_SOFT : INK_FAINT, '&:hover': { color: 'text.primary' } }}
        >
          <EditCalendarIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2.5, width: 310, border: `1px solid ${LINE}` } } }}
      >
        <Box component="span" sx={{ ...monoLabel, display: 'block', mb: 0.5 }}>
          {STAGE_EDITOR.title}
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
          {stage.name}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
            disabled={busy}
            onClick={() => void save({ finished_on: today })}
          >
            {STAGE_EDITOR.finishToday}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => void save({ started_on: today })}
          >
            {STAGE_EDITOR.startToday}
          </Button>
        </Stack>

        <Stack direction="row" spacing={1.5}>
          <TextField
            label={STAGE_EDITOR.startedOn}
            type="date"
            size="small"
            fullWidth
            value={started}
            onChange={(e) => setStarted(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={STAGE_EDITOR.finishedOn}
            type="date"
            size="small"
            fullWidth
            value={finished}
            onChange={(e) => setFinished(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>

        <TextField
          label={STAGE_EDITOR.note}
          placeholder={STAGE_EDITOR.notePlaceholder}
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ mt: 1.5 }}
        />

        <Typography
          sx={{ fontFamily: FONT_MONO, fontSize: '0.625rem', color: INK_FAINT, mt: 1.25 }}
        >
          {STAGE_EDITOR.emptyKeeps}
        </Typography>

        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', mt: 1.25 }}>
            {error}
          </Typography>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button size="small" color="inherit" disabled={busy || !captured} onClick={() => void clear()}>
            {STAGE_EDITOR.clear}
          </Button>
          <Stack direction="row" spacing={1}>
            <Button size="small" color="inherit" disabled={busy} onClick={close}>
              {COMMON.close}
            </Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              disabled={busy}
              onClick={() => void saveForm()}
            >
              {busy ? STAGE_EDITOR.saving : STAGE_EDITOR.save}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
